/**
 * RENDER — builds the visual course from a CourseSpec.
 *
 * Static elements (track / wall / ramp / booster / hazard) are baked into a small
 * number of MERGED geometries — one per material/kind — so the whole static world
 * is a handful of draw calls on mobile. Movers are kept as individual meshes
 * because they animate every frame; their pose is recomputed as a pure function of
 * the sim tick per the FROZEN `MoverMotion` contract (never via runtime randomness).
 *
 * Also extracts world-space wall segments and booster/ramp volumes so the FX layer
 * can derive near-miss and shortcut juice from geometry WE own — never from input.
 *
 * Owns all geometries/materials it creates and frees them in `dispose()`.
 */

import * as THREE from 'three';
import type { CourseSpec, CourseElement, MoverElement, Vec3 } from '@/types';
import { PALETTE } from './theme';
import { moverOffset, type MVec3 } from './math';
import { disposeMaterial } from './three-util';

/** A world-space box volume the FX layer can test the marble against. */
export interface BoxVolume {
  readonly id: number;
  readonly kind: CourseElement['kind'];
  /** World center. */
  readonly center: Vec3;
  /** Half-extents (axis-aligned approximation; good enough for proximity FX). */
  readonly halfExtents: Vec3;
}

/** Per-frame animated mover record. */
interface MoverRecord {
  readonly mesh: THREE.Mesh;
  readonly motion: MoverElement['motion'];
  /** The element's base (unanimated) position. */
  readonly basePos: Vec3;
}

/** Shared scratch so the mover update path allocates nothing. */
const scratchOffset: MVec3 = [0, 0, 0];

export class CourseView {
  /** Root group holding all course geometry; add this to the scene. */
  readonly group: THREE.Group;

  private readonly geometries: THREE.BufferGeometry[] = [];
  private readonly materials: THREE.Material[] = [];
  private readonly movers: MoverRecord[] = [];

  /** World-space wall boxes for near-miss FX. */
  readonly wallVolumes: BoxVolume[] = [];
  /** World-space booster + ramp boxes for shortcut/boost FX. */
  readonly boostVolumes: BoxVolume[] = [];

  constructor(spec: CourseSpec) {
    this.group = new THREE.Group();
    this.group.name = 'course';
    this.build(spec);
  }

  private build(spec: CourseSpec): void {
    // Bucket static elements by kind so each kind becomes one merged mesh.
    const buckets = new Map<CourseElement['kind'], CourseElement[]>();
    for (const el of spec.elements) {
      if (el.kind === 'mover') {
        this.addMover(el);
        continue;
      }
      const list = buckets.get(el.kind);
      if (list) list.push(el);
      else buckets.set(el.kind, [el]);

      if (el.kind === 'wall') {
        this.wallVolumes.push(toBoxVolume(el));
      } else if (el.kind === 'booster' || el.kind === 'ramp') {
        this.boostVolumes.push(toBoxVolume(el));
      }
    }

    for (const [kind, list] of buckets) {
      const merged = mergeBoxes(list);
      this.geometries.push(merged);
      const material = this.makeMaterial(kind);
      const mesh = new THREE.Mesh(merged, material);
      mesh.name = `course-${kind}`;
      mesh.matrixAutoUpdate = false; // static; never moves.
      mesh.updateMatrix();
      this.group.add(mesh);
    }
  }

  private addMover(el: MoverElement): void {
    const geo = new THREE.BoxGeometry(
      el.halfExtents[0] * 2,
      el.halfExtents[1] * 2,
      el.halfExtents[2] * 2,
    );
    this.geometries.push(geo);
    const material = this.makeMaterial('mover');
    const mesh = new THREE.Mesh(geo, material);
    mesh.name = `mover-${el.id}`;
    mesh.quaternion.set(
      el.transform.rotation[0],
      el.transform.rotation[1],
      el.transform.rotation[2],
      el.transform.rotation[3],
    );
    mesh.position.set(
      el.transform.position[0],
      el.transform.position[1],
      el.transform.position[2],
    );
    this.group.add(mesh);
    this.movers.push({ mesh, motion: el.motion, basePos: el.transform.position });
  }

  /**
   * Recompute every mover's pose for `tick` (may be fractional: tick + alpha) as a
   * pure function of the tick, matching the deterministic `MoverMotion` contract.
   * Allocates nothing.
   */
  updateMovers(tick: number): void {
    for (const m of this.movers) {
      moverOffset(scratchOffset, m.motion, tick);
      m.mesh.position.set(
        m.basePos[0] + scratchOffset[0],
        m.basePos[1] + scratchOffset[1],
        m.basePos[2] + scratchOffset[2],
      );
    }
  }

  private makeMaterial(kind: CourseElement['kind']): THREE.Material {
    const color = elementColorForKind(kind);
    let mat: THREE.MeshStandardMaterial;
    switch (kind) {
      case 'track':
        mat = new THREE.MeshStandardMaterial({
          color,
          roughness: 0.85,
          metalness: 0.0,
          emissive: PALETTE.cyan,
          emissiveIntensity: 0.025, // faint cyan wash so edges read in the dark
        });
        break;
      case 'wall':
        mat = new THREE.MeshStandardMaterial({
          color,
          roughness: 0.6,
          metalness: 0.1,
          emissive: PALETTE.wallLip,
          emissiveIntensity: 0.18, // glowing boundary, legible at speed
        });
        break;
      case 'ramp':
        mat = new THREE.MeshStandardMaterial({
          color: PALETTE.track,
          roughness: 0.7,
          metalness: 0.0,
          emissive: PALETTE.ramp,
          emissiveIntensity: 0.3,
        });
        break;
      case 'booster':
        mat = new THREE.MeshStandardMaterial({
          color,
          roughness: 0.4,
          metalness: 0.0,
          emissive: PALETTE.booster,
          emissiveIntensity: 0.8, // "go faster" should glow hot
        });
        break;
      case 'hazard':
        mat = new THREE.MeshStandardMaterial({
          color,
          roughness: 0.5,
          metalness: 0.0,
          emissive: PALETTE.hazard,
          emissiveIntensity: 0.7,
        });
        break;
      case 'mover':
        mat = new THREE.MeshStandardMaterial({
          color,
          roughness: 0.55,
          metalness: 0.15,
          emissive: PALETTE.mover,
          emissiveIntensity: 0.45,
        });
        break;
    }
    this.materials.push(mat);
    return mat;
  }

  dispose(): void {
    for (const geo of this.geometries) geo.dispose();
    for (const mat of this.materials) disposeMaterial(mat);
    this.geometries.length = 0;
    this.materials.length = 0;
    this.movers.length = 0;
    this.wallVolumes.length = 0;
    this.boostVolumes.length = 0;
    this.group.removeFromParent();
    this.group.clear();
  }
}

/** Kind → color, kept here so `makeMaterial` stays readable. */
function elementColorForKind(kind: CourseElement['kind']): number {
  switch (kind) {
    case 'track':
      return PALETTE.track;
    case 'wall':
      return PALETTE.wall;
    case 'ramp':
      return PALETTE.ramp;
    case 'booster':
      return PALETTE.booster;
    case 'hazard':
      return PALETTE.hazard;
    case 'mover':
      return PALETTE.mover;
  }
}

/**
 * Merge a list of box elements into one BufferGeometry, each box transformed into
 * world space. This is the draw-call reduction that keeps the static world cheap on
 * mobile. We build boxes by hand (no examples/jsm dependency) and bake transforms
 * via a Matrix4 so rotated ramps land correctly.
 */
function mergeBoxes(elements: readonly CourseElement[]): THREE.BufferGeometry {
  const matrix = new THREE.Matrix4();
  const quat = new THREE.Quaternion();
  const pos = new THREE.Vector3();
  const scl = new THREE.Vector3();
  const parts: THREE.BufferGeometry[] = [];

  for (const el of elements) {
    const box = new THREE.BoxGeometry(
      el.halfExtents[0] * 2,
      el.halfExtents[1] * 2,
      el.halfExtents[2] * 2,
    );
    quat.set(
      el.transform.rotation[0],
      el.transform.rotation[1],
      el.transform.rotation[2],
      el.transform.rotation[3],
    );
    pos.set(el.transform.position[0], el.transform.position[1], el.transform.position[2]);
    scl.set(1, 1, 1);
    matrix.compose(pos, quat, scl);
    box.applyMatrix4(matrix);
    parts.push(box);
  }

  const merged = mergeBufferGeometries(parts);
  for (const p of parts) p.dispose();
  return merged;
}

/**
 * Minimal, self-contained merge of position+normal+uv buffers from a set of
 * identically-attributed non-indexed... here BoxGeometry is indexed, so we expand
 * indices to keep the merge trivial and dependency-free. Result is a single
 * non-indexed geometry with position + normal attributes (uv dropped; unused by our
 * flat/emissive materials). Good enough and avoids the examples/jsm import.
 */
function mergeBufferGeometries(parts: readonly THREE.BufferGeometry[]): THREE.BufferGeometry {
  let totalVerts = 0;
  const expanded: { position: Float32Array; normal: Float32Array }[] = [];

  for (const geo of parts) {
    const posAttr = geo.getAttribute('position') as THREE.BufferAttribute;
    const normAttr = geo.getAttribute('normal') as THREE.BufferAttribute | undefined;
    const index = geo.getIndex();
    const count = index ? index.count : posAttr.count;
    const position = new Float32Array(count * 3);
    const normal = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      const vi = index ? index.getX(i) : i;
      position[i * 3 + 0] = posAttr.getX(vi);
      position[i * 3 + 1] = posAttr.getY(vi);
      position[i * 3 + 2] = posAttr.getZ(vi);
      if (normAttr) {
        normal[i * 3 + 0] = normAttr.getX(vi);
        normal[i * 3 + 1] = normAttr.getY(vi);
        normal[i * 3 + 2] = normAttr.getZ(vi);
      }
    }
    expanded.push({ position, normal });
    totalVerts += count;
  }

  const position = new Float32Array(totalVerts * 3);
  const normal = new Float32Array(totalVerts * 3);
  let offset = 0;
  for (const e of expanded) {
    position.set(e.position, offset);
    normal.set(e.normal, offset);
    offset += e.position.length;
  }

  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(position, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(normal, 3));
  out.computeBoundingSphere();
  return out;
}

/** World-space AABB-ish volume for an element (ignores rotation extent growth — fine for FX). */
function toBoxVolume(el: CourseElement): BoxVolume {
  return {
    id: el.id,
    kind: el.kind,
    center: el.transform.position,
    halfExtents: el.halfExtents,
  };
}
